import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { authService } from '../services/authService';
import { hasPagePermission, PagePermissions } from '../constants/pages';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: keyof PagePermissions; // Optional: specific permission required
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredPermission }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const isAuthenticated = authService.isAuthenticated();
  const isAdmin = authService.isAdmin();
  const permissions = authService.getPermissions();
  const currentPath = location.pathname;

  // Fetch user profile with permissions on mount (if authenticated)
  useEffect(() => {
    const loadUserProfile = async () => {
      if (isAuthenticated) {
        // Check if permissions are already loaded
        const existingPermissions = authService.getPermissions();
        const hasStoredPermissions = Object.values(existingPermissions).some(v => v === true);
        
        if (!hasStoredPermissions && !isAdmin) {
          // Fetch user profile to get permissions
          await authService.fetchUserProfile();
        }
        setPermissionsLoaded(true);
      }
      setLoading(false);
    };

    loadUserProfile();
  }, [isAuthenticated, isAdmin]);

  // Show loading while fetching permissions
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // If the user is not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin has full access
  if (isAdmin) {
    return <>{children}</>;
  }

  // If specific permission is required, check it
  if (requiredPermission) {
    if (!permissions[requiredPermission]) {
      return <Navigate to="/access-denied" replace />;
    }
    return <>{children}</>;
  }

  // Check page permission based on current path
  if (!hasPagePermission(currentPath, permissions)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
